import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import moment from 'moment';
import { ReferentielService } from 'src/app/services/referentiel/referentiel.service';
import { ReferentielEnum } from 'src/app/utils/enums/referentiel-enum';
import { HistoriqueIdentite } from 'src/app/utils/interfaces/new/HistoriqueIdentite';
import { InfosPatientJournal } from 'src/app/utils/interfaces/new/infos-patient-journal.interface';
import { environment } from 'src/environments/environment';

interface IdentiteHistoriqueNode {
  name: string;
  value: string;
  children?: IdentiteHistoriqueNode[];
}

interface IdentiteHistoriqueFlatNode {
  expandable: boolean;
  name: string;
  value: string;
  level: number;
}

@Component({
  selector: 'app-historique-patient',
  templateUrl: './historique-patient.component.html',
  styleUrls: [
    './historique-patient.component.scss',
    '../modal.fragment.scss'
  ]
})
export class HistoriquePatientComponent implements OnInit {

  public _patientJournal: InfosPatientJournal;
  public set patientJournal(journal: InfosPatientJournal | undefined) {
    if (journal) {
      this._initJournalPatient(journal);
    }
  }
  public set identiteHistorique(histo: Array<HistoriqueIdentite>) {
    if (histo) {
      this._initIdentiteHistorique(histo);
    }
  };

  public _patientPoids: string;
  public _patientTaille: string;
  public _patientCreatine: string;
  public _patientDFG: string;
  public _patientInsHep: string;
  public _patientGrossesse: string;
  public _patientAllaitement: string;
  public _patientAmenorrhee: string;

  @ViewChild('modalBody') public modalBody: ElementRef;
  @ViewChild('outsideModal') public outsideModal: ElementRef;

  @Output() public closeEmitter = new EventEmitter();

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event): void {
    if (!environment.closeModalOnClickOutside) {
      return;
    }

    if (!this.modalBody.nativeElement.contains(event.target) && this.outsideModal.nativeElement.contains(event.target)) {
      this.closeModal(false);
    }
  }

  constructor() { }

  ngOnInit(): void {
  }

  public closeModal(emit: boolean): void {
    this.closeEmitter.emit();
  }

  private _initJournalPatient(journal: InfosPatientJournal) {
    if (journal.poidsEditedAt && journal.poidsEditedBy) {
      this._patientPoids = `- Poids : Le ${moment(journal.poidsEditedAt).format("DD/MM/YYYY")} par ${journal.poidsEditedBy}`;
    }
    if (journal.tailleEditedAt && journal.tailleEditedBy) {
      this._patientTaille = `- Taille : Le ${moment(journal.tailleEditedAt).format("DD/MM/YYYY")} par ${journal.tailleEditedBy}`;
    }
    if (journal.creatEditedAt && journal.creatEditedBy) {
      this._patientCreatine = `- Créatinine : Le ${moment(journal.creatEditedAt).format("DD/MM/YYYY")} par ${journal.creatEditedBy}`;
    }
    if (journal.dfgEditedAt && journal.dfgEditedBy) {
      this._patientDFG = `- DFG : Le ${moment(journal.dfgEditedAt).format("DD/MM/YYYY")} par ${journal.dfgEditedBy}`;
    }
    if (journal.insHepatEditedAt && journal.insHepatEditedBy) {
      this._patientInsHep = `- Ins. hépatique : Le ${moment(journal.insHepatEditedAt).format("DD/MM/YYYY")} par ${journal.insHepatEditedBy}`;
    }
    if (journal.grossesseEditedAt && journal.grossesseEditedBy) {
      this._patientGrossesse = `- Grossesse : Le ${moment(journal.grossesseEditedAt).format("DD/MM/YYYY")} par ${journal.grossesseEditedBy}`;
    }
    if (journal.allaitementEditedAt && journal.allaitementEditedBy) {
      this._patientAllaitement = `- Allaitement : Le ${moment(journal.allaitementEditedAt).format("DD/MM/YYYY")} par ${journal.allaitementEditedBy}`;
    }
    if (journal.amenorrheeEditedAt && journal.amenorrheeEditedBy) {
      this._patientAmenorrhee = `- Aménorrhée : Le ${moment(journal.amenorrheeEditedAt).format("DD/MM/YYYY")} par ${journal.amenorrheeEditedBy}`;
    }

    this._patientJournal = journal;
  }

  private _initIdentiteHistorique(histo: Array<HistoriqueIdentite>): void {
    const _getDataChildren = (h: HistoriqueIdentite): Array<IdentiteHistoriqueNode> => {
      const rtd: Array<IdentiteHistoriqueNode> = [];

      rtd.push({ name: `Type`, value: h.type ? h.type : 'inconnu' });

      if (h.newIdentite && h.previousIdentite) {
        if (h.newIdentite.dateNaissance) {
          rtd.push({
            name: `Date de naissance`,
            value: `${moment(h.newIdentite.dateNaissance).format('DD-MM-YYYY')}${h.previousIdentite.dateNaissance ? ' (avant : ' + moment(h.previousIdentite.dateNaissance).format('DD-MM-YYYY') + ')' : ''}`
          });
        }
        if (h.newIdentite.documentDeValidation && h.newIdentite.documentDeValidation !== '') {
          rtd.push({
            name: `Document de validation`,
            value: `${h.newIdentite.documentDeValidation} ${h.previousIdentite.documentDeValidation && h.previousIdentite.documentDeValidation !== '' ? '(avant : ' + h.previousIdentite.documentDeValidation + ')' : ''}`
          });
        }
        if (h.newIdentite.ins && h.newIdentite.ins !== '') {
          rtd.push({
            name: `Matricule INS`,
            value: `${h.newIdentite.ins} ${h.previousIdentite.ins && h.previousIdentite.ins !== '' ? '(avant : ' + h.previousIdentite.ins + ')' : ''}`
          });
        }
        if (h.newIdentite.insKey && h.newIdentite.insKey !== '') {
          rtd.push({
            name: `Clé INS`,
            value: `${h.newIdentite.insKey} ${h.previousIdentite.insKey && h.previousIdentite.insKey !== '' ? '(avant : ' + h.previousIdentite.insKey + ')' : ''}`
          });
        }
        if (h.newIdentite.nomNaissance && h.newIdentite.nomNaissance !== '') {
          rtd.push({
            name: `Nom de naissance`,
            value: `${h.newIdentite.nomNaissance} ${h.previousIdentite.nomNaissance && h.previousIdentite.nomNaissance !== '' ? '(avant : ' + h.previousIdentite.nomNaissance + ')' : ''}`
          });
        }
        if (h.newIdentite.oid && h.newIdentite.oid !== '') {
          rtd.push({
            name: `OID`,
            value: `${h.newIdentite.oid} ${h.previousIdentite.oid && h.previousIdentite.oid !== '' ? '(avant : ' + h.previousIdentite.oid + ')' : ''}`
          });
        }
        if (h.newIdentite.prenom && h.newIdentite.prenom !== '') {
          rtd.push({
            name: `1er prénom de naissance`,
            value: `${h.newIdentite.prenom} ${h.previousIdentite.prenom && h.previousIdentite.prenom !== '' ? '(avant : ' + h.previousIdentite.prenom + ')' : ''}`
          });
        }
        if (h.newIdentite.sexe) {
          rtd.push({
            name: `Sexe`,
            value: `${h.newIdentite.sexe} ${h.previousIdentite.sexe ? '(avant : ' + h.previousIdentite.sexe + ')' : ''}`
          });
        }
        if (h.newIdentite.statut && h.newIdentite.statut !== '') {
          rtd.push({
            name: `Statut`,
            value: `${h.newIdentite.statut} ${h.previousIdentite.statut && h.previousIdentite.statut !== '' ? '(avant : ' + h.previousIdentite.statut + ')' : ''}`
          });
        }
      } else if (h.newIdentite && !h.previousIdentite) {
        if (h.newIdentite.dateNaissance) {
          rtd.push({
            name: `Date de naissance`,
            value: `${moment(h.newIdentite.dateNaissance).format('DD-MM-YYYY')}`
          });
        }
        if (h.newIdentite.documentDeValidation && h.newIdentite.documentDeValidation !== '') {
          rtd.push({
            name: `Document de validation`,
            value: `${h.newIdentite.documentDeValidation}`
          });
        }
        if (h.newIdentite.ins && h.newIdentite.ins !== '') {
          rtd.push({
            name: `Matricule INS`,
            value: `${h.newIdentite.ins}`
          });
        }
        if (h.newIdentite.insKey && h.newIdentite.insKey !== '') {
          rtd.push({
            name: `Clé INS`,
            value: `${h.newIdentite.insKey}`
          });
        }
        if (h.newIdentite.nomNaissance && h.newIdentite.nomNaissance !== '') {
          rtd.push({
            name: `Nom de naissance`,
            value: `${h.newIdentite.nomNaissance}`
          });
        }
        if (h.newIdentite.oid && h.newIdentite.oid !== '') {
          rtd.push({
            name: `OID`,
            value: `${h.newIdentite.oid}`
          });
        }
        if (h.newIdentite.prenom && h.newIdentite.prenom !== '') {
          rtd.push({
            name: `1er prénom de naissance`,
            value: `${h.newIdentite.prenom}`
          });
        }
        if (h.newIdentite.sexe) {
          rtd.push({
            name: `Sexe`,
            value: `${h.newIdentite.sexe}`
          });
        }
        if (h.newIdentite.statut && h.newIdentite.statut !== '') {
          rtd.push({
            name: `Statut`,
            value: `${h.newIdentite.statut}`
          });
        }
      }

      return rtd;
    };

    const data: Array<IdentiteHistoriqueNode> = [];
    for (const h of histo) {
      data.push({
        name: `${h.date ? 'Le ' + moment(h.date).format('DD-MM-YYYY à HH:mm') : 'date et heure inconnues'} par ${h.responsable ? h.responsable.libelle : 'Auteur inconnu'}`,
        value: '',
        children: _getDataChildren(h)
      });
    }

    this.dataSource.data = data;
  }

  private _transformer = (node: IdentiteHistoriqueNode, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level: level,
      value: node.value
    };
  };

  public treeControl = new FlatTreeControl<IdentiteHistoriqueFlatNode>(
    node => node.level,
    node => node.expandable,
  );

  private treeFlattener = new MatTreeFlattener(
    this._transformer,
    node => node.level,
    node => node.expandable,
    node => node.children,
  );

  public dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  public hasChild = (_: number, node: IdentiteHistoriqueFlatNode) => node.expandable;

}
